/**
 * Ponte entre os arrays do React Flow e o tipo `Flow` do engine.
 *
 * Dois cuidados que este arquivo existe para centralizar:
 *
 * 1. **O schema é `.strict()`.** O React Flow enfeita cada nó com `selected`, `dragging`,
 *    `measured`, `width`… Persistir o objeto cru faria a validação recusar a publicação. Então
 *    `fromReactFlow` reduz tudo a `{id, type, position, data}` e `{id, source, target, handles}`.
 *
 * 2. **Os dados do bloco vão embrulhados em `{ block }`.** Não é capricho: `Node<T>` do React Flow
 *    exige `T extends Record<string, unknown>`, e interfaces não ganham index signature implícita
 *    em TypeScript. Embrulhar num type alias resolve sem espalhar `as any`, e de brinde mantém o
 *    formato persistido idêntico ao do schema.
 *
 * Os ids de handle vêm das constantes do engine — o painel não inventa string de handle, senão a
 * aresta desenhada aqui não é a aresta que o interpretador segue.
 */

import { addEdge, MarkerType, type Connection, type Edge, type Node } from '@xyflow/react';
import {
  DEFAULT_HANDLE,
  ERROR_HANDLE,
  FALSE_HANDLE,
  TRUE_HANDLE,
  type BlockData,
  type BlockDataMap,
  type BlockKind,
  type ButtonsBlockData,
  type Flow,
  type FlowNode,
} from '@merlin/flow-engine';

/**
 * `isStart` é adorno de renderização, não conteúdo: `fromReactFlow` persiste apenas `data.block`,
 * então marcar o nó inicial aqui não contamina o que vai para o Firestore.
 */
export type BlockNodeData<K extends BlockKind = BlockKind> = {
  block: BlockDataMap[K];
  isStart?: boolean;
  /** Sem caminho desde o bloco inicial. Marcado no canvas; não impede publicar. */
  unreachable?: boolean;
  /** Portas já ligadas, para desenhar tomada vazia vs. preenchida. */
  connections?: NodeConnectionState;
};
export type AnyBlockNode = Node<BlockNodeData, BlockKind>;

/**
 * A que família um bloco pertence.
 *
 * Antes cada um dos sete tipos tinha a **sua** cor: cinza, azul, violeta, âmbar, rosa, verde-água e
 * esmeralda. Duas coisas quebravam nisso. A primeira é que quatro dessas famílias já significavam
 * estado em outro lugar do painel — âmbar era "atenção" no aviso e "Condição" no canvas, esmeralda
 * era "publicado" no selo e "webhook de backend" no nó —, e cor que significa duas coisas não
 * significa nenhuma. A segunda é que sete cores não formam um código que alguém memorize; viram
 * confete.
 *
 * Três famílias, e o **ícone** distinguindo os sete tipos. A cor passa a responder a pergunta que
 * de fato se faz varrendo um canvas grande de longe: onde este fluxo conversa, onde ele decide, e
 * onde ele sai para fora.
 */
export type BlockFamily = 'conversa' | 'logica' | 'integracao';

export interface FamilyMeta {
  label: string;
  description: string;
  /** Fundo e cor do chip que carrega o ícone, no canvas, na paleta e no inspector. */
  chip: string;
}

export const BLOCK_FAMILIES: Record<BlockFamily, FamilyMeta> = {
  conversa: {
    label: 'Conversa',
    description: 'O que o visitante lê e responde.',
    // Neutro de propósito: é o corpo do fluxo, a maioria dos blocos. Cor aqui seria ruído.
    chip: 'bg-slate-100 text-slate-600',
  },
  logica: {
    label: 'Lógica',
    description: 'Decide por onde o fluxo segue.',
    chip: 'bg-violet-100 text-violet-700',
  },
  integracao: {
    label: 'Integração',
    description: 'Sai do widget: código e chamadas a outros sistemas.',
    chip: 'bg-indigo-100 text-indigo-700',
  },
};

export interface BlockMeta {
  label: string;
  description: string;
  family: BlockFamily;
}

export const BLOCK_META: Record<BlockKind, BlockMeta> = {
  message: {
    label: 'Mensagem',
    description: 'Mostra um texto e segue.',
    family: 'conversa',
  },
  input: {
    label: 'Pergunta',
    description: 'Pergunta e guarda a resposta numa variável.',
    family: 'conversa',
  },
  buttons: {
    label: 'Botões',
    description: 'Ramifica conforme o botão clicado.',
    family: 'conversa',
  },
  condition: {
    label: 'Condição',
    description: 'Avalia as variáveis e ramifica.',
    family: 'logica',
  },
  browserCode: {
    label: 'Código no navegador',
    description: 'Roda JS no navegador do visitante.',
    family: 'integracao',
  },
  browserWebhook: {
    label: 'Webhook no navegador',
    description: 'fetch disparado pelo visitante.',
    family: 'integracao',
  },
  backendWebhook: {
    label: 'Webhook no backend',
    description: 'Executado no servidor, com segredos.',
    family: 'integracao',
  },
};

/** Chip do tipo — uma consulta, para nenhum componente montar a classe por conta própria. */
export const blockChip = (type: BlockKind): string => BLOCK_FAMILIES[BLOCK_META[type].family].chip;

export const BLOCK_ORDER: BlockKind[] = [
  'message',
  'input',
  'buttons',
  'condition',
  'browserCode',
  'browserWebhook',
  'backendWebhook',
];

/** A paleta e qualquer outra lista de tipos leem daqui, para a ordem ser a mesma em todo lugar. */
export const FAMILY_ORDER: BlockFamily[] = ['conversa', 'logica', 'integracao'];

export const blocksOfFamily = (family: BlockFamily): BlockKind[] =>
  BLOCK_ORDER.filter((type) => BLOCK_META[type].family === family);

export type HandleTone = 'default' | 'true' | 'false' | 'error';

export interface HandleSpec {
  id: string;
  label: string;
  tone: HandleTone;
}

/**
 * Cor por tom de saída. É o mesmo mapa usado pela porta no bloco e pelo traço da aresta — pintar a
 * linha com a cor da porta de onde ela sai é o vínculo visual mais direto entre as duas, e era o que
 * faltava para dar para ler de onde cada conexão parte.
 *
 * Aqui a paleta de **estado** é a certa, diferente do chip da família: uma porta é um desfecho.
 * Segue os mesmos quatro tons do `Tone` do `ui.tsx` — neutro, sim, desvio, falha — e por isso o erro
 * passou de `rose-500` a `red-600`: rosa era quase-vermelho, uma quinta cor sem quinto significado.
 */
export const TONE_COLORS: Record<HandleTone, string> = {
  default: '#64748b',
  true: '#10b981',
  false: '#f59e0b',
  error: '#dc2626',
};

/** Handles de saída de um bloco. Usado pelos componentes de nó e pela troca de arestas. */
export function sourceHandles(type: BlockKind, block: BlockData): HandleSpec[] {
  if (type === 'buttons') {
    const options = (block as ButtonsBlockData).options ?? [];
    return options.map((option) => ({
      id: option.id,
      label: option.label || option.id,
      tone: 'default' as const,
    }));
  }
  if (type === 'condition') {
    return [
      { id: TRUE_HANDLE, label: 'verdadeiro', tone: 'true' },
      { id: FALSE_HANDLE, label: 'falso', tone: 'false' },
    ];
  }
  if (type === 'browserCode' || type === 'browserWebhook' || type === 'backendWebhook') {
    return [
      { id: DEFAULT_HANDLE, label: 'sucesso', tone: 'default' },
      { id: ERROR_HANDLE, label: 'erro', tone: 'error' },
    ];
  }
  return [{ id: DEFAULT_HANDLE, label: 'saída', tone: 'default' }];
}

let sequence = 0;

function nextId(type: BlockKind): string {
  sequence += 1;
  return `${type}-${Date.now().toString(36)}${sequence.toString(36)}`;
}

/**
 * Id de uma opção de botão. Gerado, nunca digitado.
 *
 * Ele é só a chave de fiação: a aresta guarda `sourceHandle` e o roteamento faz
 * `nextNodeId(nodeId, option.id)`. Nada disso aparece na interface — a porta no canvas e o rótulo da
 * aresta usam `option.label`. Expor o campo custou caro: valores foram digitados nele em vez do
 * `value`, e a variável passou a trazer o texto do botão.
 *
 * **Opaco de propósito.** Derivar do rótulo faria renomear o botão desligar o fio.
 *
 * Substitui um gerador que contava opções (`opcao_${length + 1}`) e colidia: em `[1, 2, 3]`, apagar a
 * 2 e adicionar gerava `opcao_3` de novo — duplicata recusada só na publicação, com mensagem que não
 * explicava a causa.
 */
export function createOptionId(): string {
  const aleatorio =
    globalThis.crypto?.randomUUID?.().replace(/-/g, '').slice(0, 10) ??
    Math.random().toString(36).slice(2, 12);
  return `o_${aleatorio}`;
}

/**
 * Dados iniciais de cada tipo. Valem como documentação do formato mínimo aceito pelo schema.
 *
 * O retorno é a união `BlockData`, e não `BlockDataMap[K]` genérico: com o genérico, o TypeScript
 * transforma o alvo numa interseção de todos os tipos de bloco e recusa qualquer objeto concreto.
 */
export function createBlockData(type: BlockKind): BlockData {
  switch (type) {
    case 'message':
      return { label: 'Mensagem', text: 'Escreva a mensagem aqui.' };
    case 'input':
      return {
        label: 'Pergunta',
        prompt: 'Qual é o seu nome?',
        saveAs: 'nome',
        inputType: 'text',
        placeholder: '',
      };
    case 'buttons':
      return {
        label: 'Botões',
        prompt: 'Escolha uma opção:',
        saveAs: 'opcao',
        options: [
          { id: createOptionId(), label: 'Primeira opção' },
          { id: createOptionId(), label: 'Segunda opção' },
        ],
      };
    case 'condition':
      return {
        label: 'Condição',
        mode: 'builder',
        combinator: 'and',
        rules: [{ variable: 'nome', operator: 'isNotEmpty' }],
      };
    case 'browserCode':
      return {
        label: 'Código',
        // Sandbox é o padrão de propósito: `page` só deve ser ligado conscientemente.
        executionMode: 'sandbox',
        timeoutMs: 5000,
        saveAs: 'resultado',
        code: '// `vars` traz as variáveis do fluxo. Devolva um valor com `return`.\nreturn String(vars.nome || "").toUpperCase();',
      };
    case 'browserWebhook':
      return {
        label: 'Webhook (navegador)',
        url: 'https://api.exemplo.com/endpoint',
        method: 'POST',
        headers: [],
        contentType: 'application/json',
        body: '{"nome":"{{nome}}"}',
        saveAs: 'resposta',
        timeoutMs: 10_000,
      };
    case 'backendWebhook':
      return {
        label: 'Webhook (backend)',
        url: 'https://api.exemplo.com/endpoint',
        method: 'POST',
        headers: [],
        contentType: 'application/json',
        body: '{"nome":"{{nome}}"}',
        secretRefs: [],
        saveAs: 'resposta',
        timeoutMs: 10_000,
      };
    default: {
      const exhaustive: never = type as never;
      throw new Error(`Tipo de bloco desconhecido: ${String(exhaustive)}`);
    }
  }
}

export function createBlockNode(type: BlockKind, position: { x: number; y: number }): AnyBlockNode {
  return {
    id: nextId(type),
    type,
    position,
    data: { block: createBlockData(type) },
  };
}

/**
 * Cópia de um bloco existente, com id novo e nenhuma ligação.
 *
 * Serve o caso de montar três perguntas parecidas, ou um webhook com a mesma URL, os mesmos headers e
 * os mesmos segredos mudando um campo — refazer isso a partir do bloco em branco é digitar tudo de
 * novo.
 *
 * **Os ids de opção são regenerados.** Eles são os ids dos handles de saída, e é o que o interpretador
 * procura; herdá-los faria a cópia nascer com portas homônimas às do original. Handle é escopado por
 * nó, então não haveria colisão técnica — mas duas portas com o mesmo id em blocos diferentes é
 * exatamente o tipo de coincidência que confunde na hora de depurar uma aresta que foi para o lugar
 * errado. Gerar novos custa nada.
 *
 * **Nenhuma aresta vem junto**, e isso é decisão e não economia. A de entrada não *poderia* vir: uma
 * porta de saída leva a um destino só (ver `connectReplacingHandle`), então copiar a entrada roubaria
 * a conexão do original. E copiar só as de saída deixaria a cópia meio ligada, num estado que ninguém
 * pediu. A cópia nasce solta, ao lado, selecionada — e marcada "não alcançável" no canvas, que é a
 * dica visual de que falta ligá-la.
 *
 * O clone é por JSON de propósito: `data.block` é gravado no Firestore, logo é serializável por
 * construção, e o round-trip não depende de `structuredClone` existir no ambiente de teste.
 */
export function duplicateBlockNode(node: AnyBlockNode): AnyBlockNode {
  const type = node.type as BlockKind;
  const block = JSON.parse(JSON.stringify(node.data.block)) as BlockData;

  const comOpcoes = block as { options?: { id: string }[] };
  if (Array.isArray(comOpcoes.options)) {
    comOpcoes.options = comOpcoes.options.map((option) => ({ ...option, id: createOptionId() }));
  }

  // O sufixo é o que distingue os dois no canvas e na tabela de análise, onde só o rótulo aparece.
  const rotulado = block as { label?: string };
  rotulado.label = `${rotulado.label ?? BLOCK_META[type].label} (cópia)`;

  return {
    id: nextId(type),
    type: node.type,
    // Deslocada o suficiente para não cobrir o original, perto o suficiente para não sair da tela.
    position: { x: node.position.x + 48, y: node.position.y + 48 },
    data: { block },
  };
}

export function toReactFlowNodes(flow: Flow): AnyBlockNode[] {
  return (flow.nodes ?? []).map((node) => ({
    id: node.id,
    type: node.type,
    position: node.position,
    data: { block: node.data },
  }));
}

export function toReactFlowEdges(flow: Flow): Edge[] {
  // Sem estilo aqui: cor, seta e etiqueta sao anexadas por `decorateEdges` na renderizacao, para
  // acompanharem a renomeacao de um botao sem precisar recarregar o fluxo.
  return (flow.edges ?? []).map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? null,
    targetHandle: edge.targetHandle ?? null,
  }));
}

/** Reduz os arrays do React Flow ao formato exato que o schema `.strict()` aceita. */
export function fromReactFlow(nodes: AnyBlockNode[], edges: Edge[], startNodeId: string): Flow {
  return {
    startNodeId,
    nodes: nodes.map(
      (node) =>
        ({
          id: node.id,
          type: node.type as BlockKind,
          position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
          data: node.data.block,
        }) as FlowNode,
    ),
    // Handle nulo é omitido em vez de gravado: o engine já trata ausência como saída padrão, e
    // assim o documento salvo é idêntico ao que foi carregado, sem campo de enfeite.
    edges: edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
      ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
    })),
  };
}

/**
 * Conecta respeitando a regra do interpretador: **um handle de saída leva a um destino só**.
 * O engine pega a primeira aresta que casa com o handle, então permitir duas seria desenhar uma
 * ambiguidade que o runtime resolve em silêncio. Conectar de novo troca o destino.
 */
export function connectReplacingHandle(edges: Edge[], connection: Connection): Edge[] {
  const filtered = edges.filter(
    (edge) => !(edge.source === connection.source && (edge.sourceHandle ?? null) === (connection.sourceHandle ?? null)),
  );
  return addEdge(connection, filtered);
}

/** Dados que `BranchEdge` recebe. Derivados na renderização, nunca persistidos. */
export interface BranchEdgeData {
  label: string;
  tone: HandleTone;
  [key: string]: unknown;
}

/**
 * Anexa à aresta o nome do ramo, o tom e a cor da porta de onde ela sai.
 *
 * Roda na renderização — e não na carga do fluxo — de propósito: renomear o texto de um botão tem
 * que atualizar a etiqueta da linha na hora. Nada disso chega ao Firestore, porque `fromReactFlow`
 * reduz a aresta a `{id, source, target, handles}`.
 */
export function decorateEdges(edges: Edge[], nodes: AnyBlockNode[]): Edge[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));

  return edges.map((edge) => {
    const source = byId.get(edge.source);
    const handles = source ? sourceHandles(source.type as BlockKind, source.data.block) : [];
    const handle =
      handles.find((item) => item.id === (edge.sourceHandle ?? DEFAULT_HANDLE)) ?? handles[0];

    const tone: HandleTone = handle?.tone ?? 'default';
    const color = TONE_COLORS[tone];

    return {
      ...edge,
      type: 'branch',
      data: { label: handle?.label ?? '', tone } satisfies BranchEdgeData,
      style: {
        stroke: color,
        strokeWidth: 2,
        // Ramo de erro tracejado: some com a cor para quem não distingue vermelho de cinza.
        ...(tone === 'error' ? { strokeDasharray: '6 4' } : {}),
      },
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
    };
  });
}

/**
 * Ids alcançáveis a partir do bloco inicial, por busca em largura.
 *
 * Bloco fora deste conjunto nunca roda — o visitante não tem como chegar nele. É o erro mais fácil
 * de cometer montando fluxo, e sem marcação visual ele passa despercebido até alguém reclamar que
 * "aquela pergunta não aparece".
 */
export function reachableNodeIds(
  nodes: AnyBlockNode[],
  edges: Edge[],
  startNodeId: string,
): Set<string> {
  const reachable = new Set<string>();
  if (!nodes.some((node) => node.id === startNodeId)) return reachable;

  const outgoing = new Map<string, string[]>();
  for (const edge of edges) {
    const list = outgoing.get(edge.source);
    if (list) list.push(edge.target);
    else outgoing.set(edge.source, [edge.target]);
  }

  const queue = [startNodeId];
  reachable.add(startNodeId);
  while (queue.length) {
    // O `Set` também serve de guarda contra ciclo: alvo já visto não volta para a fila.
    const current = queue.shift()!;
    for (const target of outgoing.get(current) ?? []) {
      if (reachable.has(target)) continue;
      reachable.add(target);
      queue.push(target);
    }
  }

  return reachable;
}

/** Quais portas de um bloco já têm fio ligado. Alimenta o desenho "tomada vazia" vs "ligada". */
export interface NodeConnectionState {
  connectedSourceHandles: string[];
  hasIncoming: boolean;
}

/**
 * Índice de conexões por bloco.
 *
 * Serve para o conector aparecer **vazado** quando nada está ligado nele e **preenchido** quando
 * está. É a diferença entre "onde eu puxo?" e "aqui falta ligar" — a saída livre passa a se
 * destacar sozinha, sem legenda.
 */
export function buildConnectionIndex(edges: Edge[]): Map<string, NodeConnectionState> {
  const index = new Map<string, NodeConnectionState>();

  const ensure = (nodeId: string): NodeConnectionState => {
    const existing = index.get(nodeId);
    if (existing) return existing;
    const created: NodeConnectionState = { connectedSourceHandles: [], hasIncoming: false };
    index.set(nodeId, created);
    return created;
  };

  for (const edge of edges) {
    const from = ensure(edge.source);
    const handle = edge.sourceHandle ?? DEFAULT_HANDLE;
    if (!from.connectedSourceHandles.includes(handle)) from.connectedSourceHandles.push(handle);
    ensure(edge.target).hasIncoming = true;
  }

  return index;
}
